import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import ItemRevisionRibbon from "@/components/order/ItemRevisionRibbon";
import ItemColorLine from "@/components/order/ItemColorLine";
import { itemRevisionState } from "@/lib/orderRevision";
import { isTrimCategory } from "@/lib/labelHtml";

/**
 * Controls + items picker for the label print page.
 */
export default function LabelPickerControls({
  order, selected, onToggle, onToggleAll,
  startOffset, setStartOffset,
}) {
  // Removed-upstream items are not eligible for label printing — they've been
  // dropped by Base44 but preserved for audit. Hide from the "N/M selected"
  // counter and the toggle-all target.
  const eligible = (order.items || []).filter((i) => {
    if (i.cancelled) return false;
    const st = itemRevisionState(i, order);
    return !(st && st.kind === "removed");
  });
  const allSelected = selected.size === eligible.length && eligible.length > 0;

  return (
    <Card className="p-5" data-testid="label-print-controls">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Skip first N labels</Label>
          <Input
            type="number"
            min="0"
            max="9"
            value={startOffset}
            onChange={(e) => setStartOffset(Math.max(0, Math.min(9, parseInt(e.target.value, 10) || 0)))}
            data-testid="offset-input"
            className="mt-1.5 max-w-[120px]"
          />
          <p className="text-xs text-zinc-500 mt-1">Reuse a partially-used sheet (0 – 9).</p>
        </div>
        <RulesSummary />
      </div>

      <div className="mt-5 pt-4 border-t border-zinc-200">
        <PickerHeader
          selectedCount={selected.size}
          totalCount={eligible.length}
          allSelected={allSelected}
          onToggleAll={onToggleAll}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
          {(order.items || []).map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              order={order}
              selected={selected.has(item.id)}
              onToggle={onToggle}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

function RulesSummary() {
  return (
    <div className="text-sm text-zinc-600 leading-relaxed">
      <p className="font-semibold mb-1">Label rules (matches the mobile app):</p>
      <ul className="list-disc list-inside text-xs space-y-0.5">
        <li>Cabinet items with qty &gt; 1 → one label per unit</li>
        <li>Trim/molding/skin/filler items → one label with a red <span className="font-mono">QTY:&nbsp;N</span> badge</li>
        <li>Items with modifications get a yellow <span className="font-mono">★&nbsp;MOD</span> banner</li>
      </ul>
    </div>
  );
}

function PickerHeader({ selectedCount, totalCount, allSelected, onToggleAll }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">
        Items to print ({selectedCount}/{totalCount} selected)
      </Label>
      <button
        type="button"
        className="text-xs font-semibold text-[#C8102E] hover:underline"
        onClick={onToggleAll}
        data-testid="toggle-all-items"
      >
        {allSelected ? "Deselect all" : "Select all"}
      </button>
    </div>
  );
}

function ItemRow({ item, order, selected, onToggle }) {
  const trim = isTrimCategory(item.category);
  const total = item.units_total || 1;
  const labelsForItem = trim ? 1 : total;
  const revState = itemRevisionState(item, order);
  const removed = revState && revState.kind === "removed";
  const disabled = item.cancelled || removed;
  return (
    <label
      className={`flex items-center gap-3 p-2.5 rounded-md border ${
        disabled
          ? "opacity-50 border-zinc-200"
          : "border-zinc-200 hover:border-zinc-400 cursor-pointer"
      }`}
      data-testid={`label-item-${item.sku}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => !disabled && onToggle(item.id)}
        disabled={disabled}
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-xs font-semibold">{item.sku}</div>
        <div className="text-sm flex items-center gap-1.5 min-w-0">
          <span className="truncate">{item.name}</span>
          {(item.color || item.sku) && (
            <>
              <span aria-hidden className="text-zinc-400">·</span>
              <ItemColorLine item={item} className="text-zinc-700 dark:text-zinc-300" />
            </>
          )}
        </div>
        {revState && (
          <div className="mt-1"><ItemRevisionRibbon item={item} order={order} /></div>
        )}
      </div>
      <div className="text-xs text-zinc-500 font-mono whitespace-nowrap">
        {trim && total > 1 ? `QTY ${total}` : `${labelsForItem} label${labelsForItem === 1 ? "" : "s"}`}
      </div>
    </label>
  );
}
